// src/redux/preferencesSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getMyPreferences, updateMyPreferences } from "../services/preferencesService";

import {
  DEFAULT_CITY_SLUG,
  DEFAULT_NICHE_SLUG,
  findLaunchNicheBySlug,
  labelForLaunchCitySlug,
  labelForLaunchNicheSlug,
} from "../data/slugIndex";
import {
  getLaunchNicheMode,
  resolveDiscoveryCitySlug,
  resolveDiscoveryNicheSlug,
} from "../utils/discoverySegment";

const DEFAULT_PLATFORM_SLUG = "instagram";

const safeStr = (value) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim();

const uniq = (values) => {
  const seen = new Set();
  const out = [];

  for (const value of Array.isArray(values) ? values : []) {
    const text = safeStr(value);
    if (!text) continue;

    const key = text.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    out.push(text);
  }

  return out;
};

const normalizePlatform = () => DEFAULT_PLATFORM_SLUG;

const normalizePlatforms = (values) => {
  const src = Array.isArray(values) ? values : [];
  const out = [];

  for (const value of src.length ? src : [DEFAULT_PLATFORM_SLUG]) {
    const platform = normalizePlatform(value);
    if (platform && !out.includes(platform)) out.push(platform);
  }

  return out.length ? out : [DEFAULT_PLATFORM_SLUG];
};

const normalizeCities = (values) => {
  const src = Array.isArray(values) ? values : [];
  const out = [];

  for (const value of src.length ? src : [DEFAULT_CITY_SLUG]) {
    const city = resolveDiscoveryCitySlug(value, DEFAULT_CITY_SLUG);
    if (city && !out.includes(city)) out.push(city);
  }

  return out.length ? out : [DEFAULT_CITY_SLUG];
};

const normalizeNiches = (values) => {
  const src = Array.isArray(values) ? values : [];
  const out = [];

  for (const value of src.length ? src : [DEFAULT_NICHE_SLUG]) {
    const niche = resolveDiscoveryNicheSlug(value, DEFAULT_NICHE_SLUG);
    if (niche && !out.includes(niche)) out.push(niche);
  }

  return out.length ? out : [DEFAULT_NICHE_SLUG];
};

const normalizeExcludedTags = (values) =>
  uniq(
    (Array.isArray(values) ? values : [])
      .map((value) => safeStr(value).toLowerCase().replace(/^#+/, ""))
      .filter(Boolean)
  );

const normalizeMode = (value, fallback = "child") => {
  const mode = safeStr(value).toLowerCase().replace("-", "_");
  return mode === "parent" || mode === "child" ? mode : fallback;
};

const launchModeForNiche = (slug) => getLaunchNicheMode(slug);

const launchParentForNiche = (slug) => {
  const niche = findLaunchNicheBySlug(slug);
  return niche?.parent || slug;
};

const unwrapPreferencesPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload || null;

  if (payload.preferences && typeof payload.preferences === "object") {
    return payload.preferences;
  }

  if (payload.data && typeof payload.data === "object") {
    return payload.data;
  }

  if (payload.item && typeof payload.item === "object") {
    return payload.item;
  }

  return payload;
};

const hasPreferenceData = (payload) => {
  const raw = unwrapPreferencesPayload(payload);
  if (!raw || typeof raw !== "object") return false;

  return [
    "platforms",
    "cities",
    "niches",
    "citySelections",
    "city_selections",
    "nicheSelections",
    "niche_selections",
    "feedDefaults",
    "feed_defaults",
    "activeCitySlug",
    "active_city_slug",
    "activeNicheSlug",
    "active_niche_slug",
    "selectedCitySlug",
    "selected_city_slug",
    "selectedNicheSlug",
    "selected_niche_slug",
  ].some((key) => Object.prototype.hasOwnProperty.call(raw, key));
};

const valuesFromCitySelections = (raw) => {
  const selections = Array.isArray(raw?.citySelections)
    ? raw.citySelections
    : Array.isArray(raw?.city_selections)
      ? raw.city_selections
      : [];

  return selections
    .map(
      (item) =>
        item?.slug ||
        item?.city_slug ||
        item?.citySlug ||
        item?.id ||
        item?.value
    )
    .filter(Boolean);
};

const valuesFromNicheSelections = (raw) => {
  const selections = Array.isArray(raw?.nicheSelections)
    ? raw.nicheSelections
    : Array.isArray(raw?.niche_selections)
      ? raw.niche_selections
      : [];

  return selections
    .map(
      (item) =>
        item?.slug ||
        item?.niche_slug ||
        item?.nicheSlug ||
        item?.id ||
        item?.value
    )
    .filter(Boolean);
};

const deriveCityValues = (raw) => {
  if (Array.isArray(raw?.cities) && raw.cities.length) {
    return raw.cities;
  }

  const fromSelections = valuesFromCitySelections(raw);
  if (fromSelections.length) return fromSelections;

  return [
    raw?.feedDefaults?.city ||
      raw?.feed_defaults?.city ||
      raw?.activeCitySlug ||
      raw?.active_city_slug ||
      raw?.selectedCitySlug ||
      raw?.selected_city_slug ||
      raw?.primaryCitySlug ||
      raw?.primary_city_slug ||
      raw?.citySlug ||
      raw?.city_slug ||
      raw?.locationSlug ||
      raw?.location_slug ||
      raw?.city ||
      raw?.location,
  ].filter(Boolean);
};

const deriveNicheValues = (raw) => {
  if (Array.isArray(raw?.niches) && raw.niches.length) {
    return raw.niches;
  }

  const fromSelections = valuesFromNicheSelections(raw);
  if (fromSelections.length) return fromSelections;

  return [
    raw?.feedDefaults?.niche ||
      raw?.feed_defaults?.niche ||
      raw?.activeNicheSlug ||
      raw?.active_niche_slug ||
      raw?.selectedNicheSlug ||
      raw?.selected_niche_slug ||
      raw?.primaryNicheSlug ||
      raw?.primary_niche_slug ||
      raw?.nicheSlug ||
      raw?.niche_slug ||
      raw?.preferredNicheSlug ||
      raw?.preferred_niche_slug ||
      raw?.interests ||
      raw?.industry,
  ].filter(Boolean);
};

const normalizeCitySelection = (selection, fallbackSlug = DEFAULT_CITY_SLUG) => {
  const raw =
    selection && typeof selection === "object"
      ? selection
      : { slug: selection };

  const slug = resolveDiscoveryCitySlug(
    raw.slug ||
      raw.city_slug ||
      raw.citySlug ||
      raw.id ||
      raw.value ||
      fallbackSlug,
    fallbackSlug
  );

  return {
    slug,
    name: safeStr(raw.name || raw.label) || labelForLaunchCitySlug(slug),
    isDefault: Boolean(raw.isDefault || raw.is_default),
  };
};

const normalizeCitySelections = (rawPrefs, cities) => {
  const direct =
    rawPrefs?.citySelections ||
    rawPrefs?.city_selections ||
    [];

  const selected = [
    rawPrefs?.selectedCity,
    rawPrefs?.selected_city,
    rawPrefs?.primaryCity,
    rawPrefs?.primary_city,
  ].filter(Boolean);

  const source = Array.isArray(direct) && direct.length ? direct : selected;

  const seen = new Set();
  const out = [];

  for (const item of source) {
    const selection = normalizeCitySelection(item, cities?.[0] || DEFAULT_CITY_SLUG);
    if (!selection.slug || seen.has(selection.slug)) continue;

    seen.add(selection.slug);
    out.push(selection);
  }

  for (const slug of cities || []) {
    const selection = normalizeCitySelection(slug, slug);
    if (!selection.slug || seen.has(selection.slug)) continue;

    seen.add(selection.slug);
    out.push(selection);
  }

  const result = out.length
    ? out
    : [normalizeCitySelection(DEFAULT_CITY_SLUG, DEFAULT_CITY_SLUG)];

  return result.map((item, index) => ({
    ...item,
    isDefault: index === 0,
  }));
};

const normalizeNicheSelection = (selection, fallbackSlug = DEFAULT_NICHE_SLUG) => {
  const raw =
    selection && typeof selection === "object"
      ? selection
      : { slug: selection };

  const slug = resolveDiscoveryNicheSlug(
    raw.slug ||
      raw.niche_slug ||
      raw.nicheSlug ||
      raw.id ||
      raw.value ||
      fallbackSlug,
    fallbackSlug
  );

  const fallbackMode = launchModeForNiche(slug);
  const mode = normalizeMode(raw.mode || raw.niche_mode || raw.nicheMode, fallbackMode);

  return {
    slug,
    name: safeStr(raw.name || raw.label) || labelForLaunchNicheSlug(slug),
    mode,
    parent: resolveDiscoveryNicheSlug(raw.parent, launchParentForNiche(slug)),
    isDefault: Boolean(raw.isDefault || raw.is_default),
  };
};

const normalizeNicheSelections = (rawPrefs, niches) => {
  const direct =
    rawPrefs?.nicheSelections ||
    rawPrefs?.niche_selections ||
    [];

  const selected = [
    rawPrefs?.selectedNiche,
    rawPrefs?.selected_niche,
    rawPrefs?.primaryNiche,
    rawPrefs?.primary_niche,
  ].filter(Boolean);

  const source = Array.isArray(direct) && direct.length ? direct : selected;

  const seen = new Set();
  const out = [];

  for (const item of source) {
    const selection = normalizeNicheSelection(item, niches?.[0] || DEFAULT_NICHE_SLUG);
    if (!selection.slug || seen.has(selection.slug)) continue;

    seen.add(selection.slug);
    out.push(selection);
  }

  for (const slug of niches || []) {
    const selection = normalizeNicheSelection(slug, slug);
    if (!selection.slug || seen.has(selection.slug)) continue;

    seen.add(selection.slug);
    out.push(selection);
  }

  const result = out.length
    ? out
    : [normalizeNicheSelection(DEFAULT_NICHE_SLUG, DEFAULT_NICHE_SLUG)];

  return result.map((item, index) => ({
    ...item,
    isDefault: index === 0,
  }));
};

export const normalizePreferences = (payload) => {
  const raw = unwrapPreferencesPayload(payload);

  if (!raw || typeof raw !== "object") return null;

  const platforms = normalizePlatforms(raw.platforms);
  const cities = normalizeCities(deriveCityValues(raw));
  const niches = normalizeNiches(deriveNicheValues(raw));

  const excludedTags = normalizeExcludedTags(
    raw.excludedTags || raw.excluded_tags || []
  );

  const citySelections = normalizeCitySelections(raw, cities);
  const nicheSelections = normalizeNicheSelections(raw, niches);

  const selectedCity = citySelections[0] || null;
  const selectedNiche = nicheSelections[0] || null;

  const selectedCitySlug = selectedCity?.slug || cities[0] || DEFAULT_CITY_SLUG;
  const selectedNicheSlug = selectedNiche?.slug || niches[0] || DEFAULT_NICHE_SLUG;

  const nicheMode = normalizeMode(
    raw.feedDefaults?.nicheMode ||
      raw.feed_defaults?.niche_mode ||
      raw.nicheMode ||
      raw.niche_mode ||
      selectedNiche?.mode,
    selectedNiche?.mode || launchModeForNiche(selectedNicheSlug)
  );

  const updatedAt =
    raw.updatedAt ||
    raw.updated_at ||
    raw.version ||
    raw.preferencesVersion ||
    raw.preferences_version ||
    null;

  return {
    ...raw,

    platforms,
    cities,
    niches,

    citySelections,
    city_selections: citySelections,

    excludedTags,
    excluded_tags: excludedTags,

    nicheSelections,
    niche_selections: nicheSelections,

    selectedCity,
    selected_city: selectedCity,
    primaryCity: selectedCity,
    primary_city: selectedCity,

    activeCitySlug: selectedCitySlug,
    active_city_slug: selectedCitySlug,
    selectedCitySlug,
    selected_city_slug: selectedCitySlug,
    primaryCitySlug: selectedCitySlug,
    primary_city_slug: selectedCitySlug,
    citySlug: selectedCitySlug,
    city_slug: selectedCitySlug,
    locationSlug: selectedCitySlug,
    location_slug: selectedCitySlug,
    location: selectedCitySlug,

    selectedNiche,
    selected_niche: selectedNiche,
    primaryNiche: selectedNiche,
    primary_niche: selectedNiche,

    activeNicheSlug: selectedNicheSlug,
    active_niche_slug: selectedNicheSlug,
    selectedNicheSlug,
    selected_niche_slug: selectedNicheSlug,
    primaryNicheSlug: selectedNicheSlug,
    primary_niche_slug: selectedNicheSlug,
    nicheSlug: selectedNicheSlug,
    niche_slug: selectedNicheSlug,

    nicheMode,
    niche_mode: nicheMode,

    nicheModes: nicheSelections.map((item) => item.mode),
    niche_modes: nicheSelections.map((item) => item.mode),

    nicheModeBySlug: nicheSelections.reduce((acc, item) => {
      acc[item.slug] = item.mode;
      return acc;
    }, {}),

    niche_mode_by_slug: nicheSelections.reduce((acc, item) => {
      acc[item.slug] = item.mode;
      return acc;
    }, {}),

    feedDefaults: {
      platform: platforms[0] || DEFAULT_PLATFORM_SLUG,
      city: selectedCitySlug,
      niche: selectedNicheSlug,
      nicheMode,
    },

    feed_defaults: {
      platform: platforms[0] || DEFAULT_PLATFORM_SLUG,
      city: selectedCitySlug,
      niche: selectedNicheSlug,
      niche_mode: nicheMode,
    },

    updatedAt,
    updated_at: updatedAt,
  };
};

const mergePreferences = (current, incoming) => {
  const base = current && typeof current === "object" ? current : {};
  const next = incoming && typeof incoming === "object" ? incoming : {};

  return normalizePreferences({
    ...base,
    ...next,
  });
};

export const fetchPreferencesAsync = createAsyncThunk(
  "preferences/get",
  async (_, { rejectWithValue }) => {
    try {
      const resp = await getMyPreferences();

      // Preserve the current Redux selection when an endpoint returns an empty,
      // acknowledgement-only, or older partial response.
      return hasPreferenceData(resp) ? normalizePreferences(resp) : null;
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load preferences");
    }
  },
  {
    condition: (options, { getState }) => {
      const state = getState()?.preferences || {};
      if (state.loadingGet || state.loadingPut) return false;
      return Boolean(options?.force) || !state.hydrated;
    },
  }
);

export const updatePreferencesAsync = createAsyncThunk(
  "preferences/put",
  async (body, { rejectWithValue }) => {
    try {
      const requestPreferences = normalizePreferences(body);
      const resp = await updateMyPreferences(requestPreferences || body || {});
      const responsePreferences = normalizePreferences(resp);

      // Important:
      // Some APIs return { ok: true }, a partial object, or an older wrapped object.
      // The request payload must win so the UI does not jump back to old values.
      return mergePreferences(responsePreferences || {}, requestPreferences || {});
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to update preferences");
    }
  }
);

const initialState = {
  data: null,
  hydrated: false,
  loadingGet: false,
  loadingPut: false,
  error: null,
  version: 0,
  lastFetchedAt: null,
  lastSavedAt: null,
  currentGetRequestId: null,
  currentPutRequestId: null,
};

const prefsSlice = createSlice({
  name: "preferences",
  initialState,
  reducers: {
    clearPreferencesError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ----- GET -----
      .addCase(fetchPreferencesAsync.pending, (state, action) => {
        state.loadingGet = true;
        state.error = null;
        state.currentGetRequestId = action.meta.requestId;
      })
      .addCase(fetchPreferencesAsync.fulfilled, (state, action) => {
        if (state.currentGetRequestId !== action.meta.requestId) return;

        state.loadingGet = false;
        state.currentGetRequestId = null;
        state.hydrated = true;
        state.error = null;
        state.lastFetchedAt = Date.now();

        if (action.payload) {
          state.data = action.payload;
        } else if (!state.data) {
          state.data = normalizePreferences({});
        }
      })
      .addCase(fetchPreferencesAsync.rejected, (state, action) => {
        if (state.currentGetRequestId !== action.meta.requestId) return;

        state.loadingGet = false;
        state.currentGetRequestId = null;
        state.hydrated = true;
        state.error =
          action.payload || action.error?.message || "Failed to load preferences";

        // Do not leave the app stuck after refresh if preferences GET fails.
        if (!state.data) {
          state.data = normalizePreferences({});
        }
      })

      // ----- PUT -----
      .addCase(updatePreferencesAsync.pending, (state, action) => {
        state.loadingPut = true;
        state.error = null;
        state.currentPutRequestId = action.meta.requestId;

        // A GET that started before this save is now stale. Ignore its result.
        state.currentGetRequestId = null;
        state.loadingGet = false;

        const optimistic = normalizePreferences(action.meta.arg);
        if (optimistic) {
          state.data = mergePreferences(state.data, optimistic);
          state.hydrated = true;
        }
      })
      .addCase(updatePreferencesAsync.fulfilled, (state, action) => {
        if (state.currentPutRequestId !== action.meta.requestId) return;

        state.loadingPut = false;
        state.currentPutRequestId = null;
        state.hydrated = true;
        state.error = null;

        state.data = mergePreferences(state.data, action.payload || action.meta.arg || {});
        state.version += 1;
        state.lastSavedAt = Date.now();
      })
      .addCase(updatePreferencesAsync.rejected, (state, action) => {
        if (state.currentPutRequestId !== action.meta.requestId) return;

        state.loadingPut = false;
        state.currentPutRequestId = null;
        state.error =
          action.payload || action.error?.message || "Failed to update preferences";
      });
  },
});

export const { clearPreferencesError } = prefsSlice.actions;

export default prefsSlice.reducer;

export const selectPreferences = (state) => state.preferences?.data || null;

export const selectPreferencesHydrated = (state) =>
  Boolean(state.preferences?.hydrated);

export const selectPreferencesVersion = (state) =>
  state.preferences?.version || 0;

export const selectPreferencesLoading = (state) =>
  Boolean(state.preferences?.loadingGet || state.preferences?.loadingPut);

export const selectPreferencesLoadingGet = (state) =>
  Boolean(state.preferences?.loadingGet);

export const selectPreferencesLoadingPut = (state) =>
  Boolean(state.preferences?.loadingPut);

export const selectPreferencesError = (state) =>
  state.preferences?.error || null;

export const selectPreferencesLastFetchedAt = (state) =>
  state.preferences?.lastFetchedAt || null;

export const selectPreferencesLastSavedAt = (state) =>
  state.preferences?.lastSavedAt || null;